import {
  ApiError,
  type ConsoleApi,
  type CustomerStatus,
  type EnvironmentKind,
  type HealthState,
  type SSOErrorCode,
} from '@central/api';
import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';
import { meQuery } from './api';
import {
  inboxViews,
  listSearchOf,
  parsePriorities,
  type InboxListSearch,
  type InboxSearch,
  type InboxView,
} from './lib/inbox';
import { isAuditRange, type AuditSearch } from './lib/audit';
import { isDay, isReportStatus, isTicketKind, type ReportsSearch } from './lib/reports';
import { customerTabs, type CustomerTab } from './lib/customer';
import { expiringChoices, licenseStatuses, type LicensesSearch } from './lib/license';
import { Announcements, type AnnouncementsSearch } from './routes/Announcements';
import { Audit } from './routes/Audit';
import { Catalogue } from './routes/Catalogue';
import { CustomerDetail } from './routes/CustomerDetail';
import { Customers } from './routes/Customers';
import { environmentTabs, type EnvironmentTab } from './lib/environment';
import { EnvironmentDetail } from './routes/EnvironmentDetail';
import { Environments, type EnvironmentsSearch } from './routes/Environments';
import { Inbox } from './routes/Inbox';
import { Ticket } from './routes/Ticket';
import { LicenseEditor } from './routes/LicenseEditor';
import { Licenses } from './routes/Licenses';
import { Logs, type LogsSearch } from './routes/Logs';
import { Overview } from './routes/Overview';
import { Releases } from './routes/Releases';
import { Reports } from './routes/Reports';
import { Settings } from './routes/Settings';
import { Setup } from './routes/Setup';
import { LinkSso } from './routes/LinkSso';
import { SignIn } from './routes/SignIn';
import { parseSsoError } from './lib/sso';
import { Staff } from './routes/Staff';
import { AppLayout } from './shell/AppLayout';
import { resellerMayOpen } from './shell/nav';
import { ResellerDetail } from './routes/ResellerDetail';
import { Resellers } from './routes/Resellers';
import { PartnerCustomer, PartnerCustomers } from './routes/partner/PartnerCustomers';
import { PartnerOverview } from './routes/partner/PartnerOverview';
import { PartnerEnvironment, PartnerEnvironments } from './routes/partner/PartnerEnvironments';
import { PartnerReleases } from './routes/partner/PartnerReleases';
import { PartnerReports } from './routes/partner/PartnerReports';
import { PartnerTeam } from './routes/partner/PartnerTeam';

export interface RouterContext {
  queryClient: QueryClient;
  api: ConsoleApi;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (s: Record<string, unknown>): { ssoError?: SSOErrorCode } => ({
    ssoError: parseSsoError(s.ssoError),
  }),
  component: SignIn,
});

// The step-up link a first SSO sign-in for an existing account lands on.
const linkSsoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in/link',
  component: LinkSso,
});

// The setup link's token is in the fragment; the route reads it from the location.
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  component: Setup,
});

// Every route under appRoute needs a full staff session; 401 sends the user to sign in.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    let me;
    try {
      me = await context.queryClient.ensureQueryData(meQuery(context.api));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) throw redirect({ to: '/sign-in' });
      throw e;
    }
    // A reseller reaches only its own pages; Rethink staff never use them.
    // The server enforces both (403); this keeps each from a dead page.
    const reseller = me.user.role === 'Reseller';
    if (reseller && !resellerMayOpen(location.pathname)) throw redirect({ to: '/partner' });
    if (!reseller && location.pathname.startsWith('/partner')) throw redirect({ to: '/' });
    return { me };
  },
  component: AppLayout,
});

const overviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: Overview,
});

export interface CustomersSearch {
  q?: string;
  status?: CustomerStatus;
  /** A reseller's id, or `none` for the customers Rethink supports directly. */
  resellerId?: string;
  new?: boolean;
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);

const customerStatuses: readonly string[] = [
  'active',
  'suspended',
  'archived',
] satisfies CustomerStatus[];

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers',
  validateSearch: (s: Record<string, unknown>): CustomersSearch => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    status:
      typeof s.status === 'string' && customerStatuses.includes(s.status)
        ? (s.status as CustomerStatus)
        : undefined,
    resellerId: str(s.resellerId),
    new: s.new === true || s.new === 'true' ? true : undefined,
  }),
  component: Customers,
});

const inboxListSearch = (s: Record<string, unknown>): InboxListSearch => ({
  view: typeof s.view === 'string' && s.view in inboxViews ? (s.view as InboxView) : undefined,
  tier: s.tier === 'tier1' || s.tier === 'tier2' ? s.tier : undefined,
  q: str(s.q),
  customerId: str(s.customerId),
  resellerId: str(s.resellerId),
  unread: s.unread === true || s.unread === 'true' ? true : undefined,
  tag: str(s.tag),
  priority: parsePriorities(s.priority).join(',') || undefined,
});

const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inbox',
  validateSearch: (s: Record<string, unknown>): InboxSearch => ({
    ...inboxListSearch(s),
    thread: str(s.thread),
  }),
  // A thread link (emails, bookmarks) opens the ticket's own page under the same filters.
  beforeLoad: ({ search }) => {
    if (search.thread) {
      throw redirect({
        to: '/inbox/$threadId',
        params: { threadId: search.thread },
        search: listSearchOf(search),
        replace: true,
      });
    }
  },
  component: Inbox,
});

const ticketRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inbox/$threadId',
  validateSearch: inboxListSearch,
  component: Ticket,
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  validateSearch: (s: Record<string, unknown>): ReportsSearch => ({
    from: isDay(s.from) ? s.from : undefined,
    to: isDay(s.to) ? s.to : undefined,
    customerId: str(s.customerId),
    kind: isTicketKind(s.kind) ? s.kind : undefined,
    status: isReportStatus(s.status) ? s.status : undefined,
  }),
  component: Reports,
});

const logsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/logs',
  validateSearch: (s: Record<string, unknown>): LogsSearch => ({
    customerId: str(s.customerId),
    environmentId: str(s.environmentId),
    start: str(s.start),
    end: str(s.end),
  }),
  component: Logs,
});

const announcementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/announcements',
  validateSearch: (s: Record<string, unknown>): AnnouncementsSearch => ({
    id: str(s.id),
  }),
  component: Announcements,
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  validateSearch: (s: Record<string, unknown>): AuditSearch => ({
    customerId: str(s.customerId),
    actor: str(s.actor),
    action: str(s.action),
    target: str(s.target),
    range: isAuditRange(s.range) ? s.range : undefined,
  }),
  component: Audit,
});

const licensesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/licenses',
  validateSearch: (s: Record<string, unknown>): LicensesSearch => ({
    q: str(s.q),
    customerId: str(s.customerId),
    status:
      typeof s.status === 'string' && (licenseStatuses as readonly string[]).includes(s.status)
        ? (s.status as LicensesSearch['status'])
        : undefined,
    expiring: expiringChoices.includes(Number(s.expiring)) ? Number(s.expiring) : undefined,
    new: s.new === true || s.new === 'true' ? true : undefined,
  }),
  component: Licenses,
});

const catalogueRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/catalogue',
  component: Catalogue,
});

const environmentKinds: readonly string[] = ['dedicated', 'shared'] satisfies EnvironmentKind[];
const healthStates: readonly string[] = [
  'up',
  'updating',
  'degraded',
  'down',
  'unknown',
] satisfies HealthState[];
const flag = (v: unknown) => (v === true || v === 'true' ? true : undefined);

const environmentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/environments',
  validateSearch: (s: Record<string, unknown>): EnvironmentsSearch => ({
    customerId: str(s.customerId),
    kind:
      typeof s.kind === 'string' && environmentKinds.includes(s.kind)
        ? (s.kind as EnvironmentKind)
        : undefined,
    health:
      typeof s.health === 'string' && healthStates.includes(s.health)
        ? (s.health as HealthState)
        : undefined,
    version: str(s.version),
    attention: flag(s.attention),
    archived: flag(s.archived),
    new: flag(s.new),
  }),
  component: Environments,
});

const releasesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/releases',
  component: Releases,
});

const resellersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/resellers',
  component: Resellers,
});

const resellerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/resellers/$resellerId',
  component: ResellerDetail,
});

const partnerOverviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/partner',
  component: PartnerOverview,
});

const partnerCustomersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/partner/customers',
  component: PartnerCustomers,
});

const partnerCustomerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/partner/customers/$customerId',
  component: PartnerCustomer,
});

const partnerEnvironmentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/partner/environments',
  validateSearch: (s: Record<string, unknown>): { customerId?: string; new?: boolean } => ({
    customerId: str(s.customerId),
    new: flag(s.new),
  }),
  component: PartnerEnvironments,
});

const partnerEnvironmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/partner/environments/$environmentId',
  component: PartnerEnvironment,
});

const partnerReleasesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/partner/releases',
  component: PartnerReleases,
});

const partnerReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/partner/reports',
  validateSearch: (s: Record<string, unknown>): ReportsSearch => ({
    from: isDay(s.from) ? s.from : undefined,
    to: isDay(s.to) ? s.to : undefined,
    customerId: str(s.customerId),
    kind: isTicketKind(s.kind) ? s.kind : undefined,
    status: isReportStatus(s.status) ? s.status : undefined,
  }),
  component: PartnerReports,
});

const partnerTeamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/partner/team',
  component: PartnerTeam,
});

const staffRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/staff',
  component: Staff,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: Settings,
});

const customerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers/$customerId',
  validateSearch: (s: Record<string, unknown>): { tab?: CustomerTab } => ({
    tab:
      typeof s.tab === 'string' && s.tab !== 'overview' && s.tab in customerTabs
        ? (s.tab as CustomerTab)
        : undefined,
  }),
  component: CustomerDetail,
});

const licenseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/licenses/$licenseId',
  component: LicenseEditor,
});

const environmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/environments/$environmentId',
  validateSearch: (s: Record<string, unknown>): { tab?: EnvironmentTab } => ({
    tab:
      typeof s.tab === 'string' && s.tab !== 'health' && s.tab in environmentTabs
        ? (s.tab as EnvironmentTab)
        : undefined,
  }),
  component: EnvironmentDetail,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  linkSsoRoute,
  setupRoute,
  appRoute.addChildren([
    overviewRoute,
    customersRoute,
    licensesRoute,
    catalogueRoute,
    environmentsRoute,
    inboxRoute,
    ticketRoute,
    reportsRoute,
    logsRoute,
    announcementsRoute,
    releasesRoute,
    resellersRoute,
    resellerRoute,
    partnerOverviewRoute,
    partnerCustomersRoute,
    partnerCustomerRoute,
    partnerEnvironmentsRoute,
    partnerEnvironmentRoute,
    partnerReleasesRoute,
    partnerReportsRoute,
    partnerTeamRoute,
    auditRoute,
    staffRoute,
    settingsRoute,
    customerRoute,
    licenseRoute,
    environmentRoute,
  ]),
]);

export function createAppRouter(context: RouterContext, history?: RouterHistory) {
  return createRouter({ routeTree, context, history, defaultPreload: 'intent' });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
